import { useMemo } from "react";
import DateInput from "@/components/DateInput";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, X } from "@phosphor-icons/react";
import BancoCombobox from "@/components/BancoCombobox";
import NumberInput from "@/components/ui/NumberInput";

const fmt = (n, moneda = "ARS") =>
  new Intl.NumberFormat("es-AR", { style: "currency", currency: moneda, minimumFractionDigits: 2 }).format(Number(n) || 0);

export default function CobroCombinadoEditor({ lineas, total, moneda, cajas, mediosPago, fecha, onChange }) {
  const sumaCobrado = useMemo(
    () => (lineas || []).reduce((acc, l) => acc + (Number(l.monto) || 0), 0),
    [lineas],
  );
  const restante = Math.max(0, Number(total || 0) - sumaCobrado);
  const excedido = sumaCobrado > Number(total || 0) + 0.001;

  // Opciones disponibles: prefiero mediosPago; si vacío, uso cajas.
  const opciones = mediosPago && mediosPago.length > 0
    ? mediosPago.filter((m) => m.caja_id).map((m) => ({
        value: m.caja_id, label: m.nombre, tipo: m.tipo,
      }))
    : (cajas || []).map((c) => ({ value: c.id, label: c.nombre, tipo: c.tipo }));

  const setLinea = (idx, patch) => {
    const next = lineas.map((l, i) => (i === idx ? { ...l, ...patch } : l));
    onChange(next);
  };
  const addLinea = () => {
    onChange([...(lineas || []), { medio_pago: "", monto: restante > 0 ? Number(restante.toFixed(2)) : 0, cheque_info: null }]);
  };
  const removeLinea = (idx) => {
    const next = lineas.filter((_, i) => i !== idx);
    onChange(next.length > 0 ? next : [{ medio_pago: "", monto: 0, cheque_info: null }]);
  };
  const autocompletarUltima = (idx) => {
    // Rellena el monto de la línea con el restante — útil para "cerrar" el cobro.
    const otros = lineas.reduce((a, l, i) => a + (i === idx ? 0 : Number(l.monto) || 0), 0);
    const nuevo = Math.max(0, Number(total || 0) - otros);
    setLinea(idx, { monto: Number(nuevo.toFixed(2)) });
  };

  return (
    <div className="space-y-2" data-testid="cobro-combinado-editor">
      <div className="grid grid-cols-12 gap-2 text-[10px] font-mono uppercase tracking-wider text-[#666] px-1">
        <div className="col-span-6">Medio</div>
        <div className="col-span-4 text-right">Monto</div>
        <div className="col-span-2"></div>
      </div>

      {(lineas || []).map((l, idx) => {
        const cajaSel = (cajas || []).find((c) => c.id === l.medio_pago);
        const esCheque = cajaSel?.tipo === "cheques";
        return (
          <div key={idx} className="space-y-2 border border-[#E5E5E5] rounded-sm bg-white p-2">
            <div className="grid grid-cols-12 gap-2 items-center">
              <div className="col-span-6">
                <Select value={l.medio_pago} onValueChange={(v) => setLinea(idx, { medio_pago: v, cheque_info: null })}>
                  <SelectTrigger className="rounded-sm h-8 text-[13px]" data-testid={`cobro-combinado-medio-${idx}`}>
                    <SelectValue placeholder="Elegí caja / medio…" />
                  </SelectTrigger>
                  <SelectContent>
                    {opciones.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-mono uppercase text-[#666] bg-neutral-100 px-1.5 py-0.5 rounded-sm">
                            {o.tipo}
                          </span>
                          <span>{o.label}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-4">
                <NumberInput
                  min={0}
                  step="0.01"
                  className="rounded-sm h-8 text-right font-mono"
                  value={l.monto ?? 0}
                  onChange={(e) => setLinea(idx, { monto: e.target.value === "" ? 0 : Number(e.target.value) })}
                  onDoubleClick={() => autocompletarUltima(idx)}
                  title="Doble click: completar restante"
                  data-testid={`cobro-combinado-monto-${idx}`}
                />
              </div>
              <div className="col-span-2 flex justify-end">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-red-500 hover:bg-red-50"
                  onClick={() => removeLinea(idx)}
                  data-testid={`cobro-combinado-remove-${idx}`}
                  title="Eliminar línea"
                >
                  <X size={14} />
                </Button>
              </div>
            </div>

            {esCheque && (
              <div className="border border-blue-200 bg-blue-50/50 rounded-sm p-2 space-y-2">
                <div className="text-[10px] uppercase font-mono text-blue-800 tracking-wider">Datos del cheque</div>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    className="rounded-sm h-7 font-mono text-xs"
                    placeholder="Número"
                    value={l.cheque_info?.numero || ""}
                    onChange={(e) => setLinea(idx, { cheque_info: { ...(l.cheque_info || {}), numero: e.target.value } })}
                    data-testid={`cobro-combinado-cheque-numero-${idx}`}
                  />
                  <BancoCombobox
                    testId={`cobro-combinado-cheque-banco-${idx}`}
                    value={l.cheque_info?.banco_code ? { code: l.cheque_info.banco_code, name: l.cheque_info.banco_nombre } : null}
                    onChange={(b) => setLinea(idx, { cheque_info: { ...(l.cheque_info || {}), banco_code: b.code, banco_nombre: b.name } })}
                  />
                  <DateInput
                    className="rounded-sm h-7 font-mono text-xs"
                    value={l.cheque_info?.fecha_emision || fecha}
                    onChange={(e) => setLinea(idx, { cheque_info: { ...(l.cheque_info || {}), fecha_emision: e.target.value } })}
                    data-testid={`cobro-combinado-cheque-emision-${idx}`}
                  />
                  <DateInput
                    className="rounded-sm h-7 font-mono text-xs"
                    placeholder="Fecha cobro"
                    value={l.cheque_info?.fecha_cobro || ""}
                    onChange={(e) => setLinea(idx, { cheque_info: { ...(l.cheque_info || {}), fecha_cobro: e.target.value } })}
                    data-testid={`cobro-combinado-cheque-cobro-${idx}`}
                  />
                </div>
              </div>
            )}
          </div>
        );
      })}

      <div className="flex items-center justify-between px-1">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addLinea}
          className="h-7 gap-1 text-xs"
          data-testid="cobro-combinado-add"
        >
          <Plus size={12} /> Agregar medio
        </Button>
        <div className="text-[10px] text-[#666] font-mono">Doble click al monto para completar el restante</div>
      </div>

      <div className={`rounded-sm border p-2 grid grid-cols-3 gap-2 text-[12px] font-mono ${excedido ? "bg-red-50 border-red-300" : restante > 0.01 ? "bg-amber-50 border-amber-300" : "bg-emerald-50 border-emerald-300"}`}>
        <div>
          <div className="text-[9px] uppercase tracking-wider text-[#666]">Total factura</div>
          <div className="font-semibold text-[13px]">{fmt(total, moneda)}</div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-wider text-[#666]">Cobrado ahora</div>
          <div className="font-semibold text-[13px]">{fmt(sumaCobrado, moneda)}</div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-wider text-[#666]">
            {excedido ? "Excedido" : restante > 0.01 ? "Queda en Cta. Cte." : "Cerrado"}
          </div>
          <div className={`font-semibold text-[13px] ${excedido ? "text-red-700" : ""}`}>
            {fmt(Math.abs(Number(total || 0) - sumaCobrado), moneda)}
          </div>
        </div>
      </div>

      {excedido && (
        <div className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded-sm p-2" data-testid="cobro-combinado-excedido-msg">
          El total cobrado supera al total de la factura. Ajustá los montos antes de guardar.
        </div>
      )}
    </div>
  );
}
